import { defineStore } from 'pinia';
import {
  deleteTranslation,
  fetchTranslations,
  removeTranslation,
  toggleApproval,
  updateTranslation,
} from '@/api/admin-be';
import { catchError } from '@/helpers/toast';

export type AvailableLanguage = 'en' | 'ru' | 'es' | 'fr' | 'de' | 'zh' | 'ja';

type SelectionOptionDefinition = {
  value: string;
  name: string;
};

export type ItemTranslation = {
  id: string;
  en: string;
  validations: null | { [key: string]: boolean };
  sport_name?: string;
  market_name?: string;
  selection_name?: string;
  sport_id?: number;
  sport_ids?: number[];
} & {
  [key in AvailableLanguage]: string;
};

type TranslationsStoreState = {
  languages: SelectionOptionDefinition[];
  types: SelectionOptionDefinition[];

  translationsLoading: boolean;
  translations: null | ItemTranslation[];
  translationsCount: number;
  translationsOffset: number;
  translationsPageSize: number;
};

export const useTranslationsStore = defineStore('translations', {
  state: () =>
    ({
      languages: [
        { value: 'en', name: 'English (Английский)' },
        { value: 'ru', name: 'Русский (Русский)' },
        { value: 'es', name: 'Español (Испанский)' },
        { value: 'fr', name: 'Français (Французский)' },
        { value: 'de', name: 'Deutsch (Немецкий)' },
        { value: 'zh', name: '中文 (Китайский)' },
        { value: 'ja', name: '日本語 (Японский)' },
      ],
      types: [
        { value: 'interface', name: 'Интерфейс' },
        { value: 'region', name: 'Регион' },
        { value: 'sport', name: 'Спорт' },
        { value: 'season', name: 'Сезон' },
        { value: 'round', name: 'Раунд' },
        { value: 'venue', name: 'Место проведения' },
        { value: 'competition', name: 'Турнир' },
        { value: 'fixture', name: 'Матч' },
        { value: 'marketType', name: 'Тип маркета' },
        { value: 'market', name: 'Маркет' },
        { value: 'outcome', name: 'Тип коэффициента' },
        { value: 'selection', name: 'Коэффициент' },
      ],

      translationsLoading: false,
      translations: null,
      translationsCount: 0,
      translationsOffset: 0,
      translationsPageSize: 15,
    }) as TranslationsStoreState,
  actions: {
    async fetchTranslations(filters?: any) {
      this.translationsLoading = true;
      const paginatedFilters = {
        ...filters,
        offset: this.translationsOffset,
        pageSize: this.translationsPageSize,
      };

      return fetchTranslations(paginatedFilters)
        .then((result) => {
          const page = Math.floor(this.translationsOffset / this.translationsPageSize);
          const maxPage = Math.floor(result.count / this.translationsPageSize);

          if (page > maxPage) {
            this.translationsOffset = 0;
            this.fetchTranslations(filters);
            return;
          }

          this.translations = result.translations;
          this.translationsCount = result.count;
        })
        .catch((error) => catchError('Ошибка загрузки переводов', error))
        .finally(() => {
          this.translationsLoading = false;
        });
    },
    async updateTranslation(id: string, update: { lang: AvailableLanguage; translation: string }) {
      return updateTranslation(id, update).catch((error) =>
        catchError('Ошибка загрузки переводов', error)
      );
    },
    async removeTranslation(locale: string, id: string) {
      return removeTranslation(locale, id).catch((error) =>
        catchError('Ошибка очистки перевода', error)
      );
    },
    async deleteTranslation(type: string, id: string) {
      return deleteTranslation(type, id).catch((error) =>
        catchError('Ошибка удаления перевода', error)
      );
    },
    async toggleApproval(id: string, locale: AvailableLanguage) {
      return toggleApproval(id, locale).catch((error) =>
        catchError('Ошибка подтверждения перевода', error)
      );
    },
  },
});
